import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Compact loading / error / empty state for use INSIDE a card or panel.
 * Companion to the page-level <ErrorState />, which centers vertically and
 * takes the full page; PanelState fits snugly into a small section.
 *
 * Renders one of three states based on which prop is set:
 *  - `loading` (boolean)        → muted-text spinner row
 *  - `error` (Error | null/undefined) → destructive message + optional Retry
 *  - `empty` (string)           → muted-text empty message
 *
 * Pass at most one of `loading` / `error` / `empty` per call. The caller is
 * responsible for ordering the checks (typical pattern: loading → error →
 * empty → success).
 */
interface PanelStateProps {
  loading?: boolean;
  loadingText?: string;
  error?: unknown;
  errorMessage?: string;
  onRetry?: () => void;
  empty?: boolean;
  emptyMessage?: string;
  className?: string;
}

export function PanelState({
  loading,
  loadingText = "Loading…",
  error,
  errorMessage = "Failed to load.",
  onRetry,
  empty,
  emptyMessage = "No data on file.",
  className,
}: PanelStateProps) {
  const base = `py-4 text-sm ${className ?? ""}`;

  if (loading) {
    return <p className={`${base} text-muted-foreground`}>{loadingText}</p>;
  }

  if (error) {
    return (
      <div className={`${base} flex items-start gap-2`}>
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
        <div>
          <p className="text-destructive">{errorMessage}</p>
          {onRetry && (
            <Button
              variant="outline"
              size="sm"
              onClick={onRetry}
              className="mt-2 h-7 px-2.5 text-xs"
            >
              Try again
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (empty) {
    return <p className={`${base} text-muted-foreground`}>{emptyMessage}</p>;
  }

  return null;
}
